<template>
  <div
    class="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm"
  >
    <form @submit.prevent="submitForm">
      <!-- Section: Details -->
      <div class="p-6">
        <div
          class="text-navy-400 mb-4 flex items-center gap-1.5 text-[10.5px] font-bold tracking-wider uppercase"
        >
          <svg
            class="text-navy-300 h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            viewBox="0 0 14 14"
          >
            <rect x="2" y="2" width="10" height="10" rx="2" />
            <path d="M5 5h4M5 7h3" />
          </svg>
          Details
        </div>

        <div>
          <label
            for="description"
            class="text-navy-700 mb-1.5 block text-[13px] font-semibold"
          >
            Description
            <span class="ml-0.5 text-red-500">*</span>
          </label>
          <textarea
            id="description"
            v-model="formData.description"
            name="description"
            :class="[
              'border-navy-100 text-navy-800 placeholder-navy-300 block w-full resize-y rounded-md border-[1.5px] px-3.5 py-2.5 text-sm transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10 focus:outline-none',
              formErrors?.description
                ? 'border-red-500 focus:border-red-500'
                : '',
            ]"
            placeholder="1847 Maple Ridge Drive&#10;Maumelle, AR 72113"
            rows="3"
            required
          ></textarea>
          <p class="text-navy-400 mt-1.5 text-xs">
            First line becomes the order headline. Second line appears below it.
          </p>
          <p
            v-if="formErrors?.description"
            class="mt-1.5 flex items-center text-sm text-red-600"
          >
            <i class="pi pi-exclamation-circle mr-2 text-xs"></i>
            {{ formErrors.description }}
          </p>
        </div>
      </div>

      <!-- Section: Assignment -->
      <div class="border-t border-neutral-200 p-6">
        <div
          class="text-navy-400 mb-4 flex items-center gap-1.5 text-[10.5px] font-bold tracking-wider uppercase"
        >
          <svg
            class="text-navy-300 h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            viewBox="0 0 14 14"
          >
            <circle cx="7" cy="5" r="3" />
            <path d="M2 13a5 5 0 0 1 10 0" />
          </svg>
          Assignment
        </div>

        <div>
          <label
            for="officerId"
            class="text-navy-700 mb-1.5 block text-[13px] font-semibold"
          >
            Officer
            <span class="ml-0.5 text-red-500">*</span>
          </label>
          <div class="relative">
            <span
              v-if="formData.officerId"
              class="bg-navy-700 pointer-events-none absolute top-1/2 left-3 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-[9px] font-bold tracking-wide text-white"
            >
              {{ selectedOfficerInitials }}
            </span>
            <select
              id="officerId"
              v-model="formData.officerId"
              name="officerId"
              :class="[
                'border-navy-100 block w-full cursor-pointer appearance-none rounded-md border-[1.5px] bg-white py-2.5 pr-10 text-sm transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10 focus:outline-none',
                formData.officerId ? 'pl-11' : 'pl-3.5',
                formErrors?.officerId
                  ? 'border-red-500 focus:border-red-500'
                  : '',
              ]"
              required
            >
              <option value="" disabled>Select an officer</option>
              <option
                v-for="member in members"
                :key="member.id"
                :value="member.id"
              >
                {{ member.name }}
              </option>
            </select>
          </div>
          <p
            v-if="formErrors?.officerId"
            class="mt-1.5 flex items-center text-sm text-red-600"
          >
            <i class="pi pi-exclamation-circle mr-2 text-xs"></i>
            {{ formErrors.officerId }}
          </p>
        </div>
      </div>

      <!-- Section: Settings -->
      <div class="border-t border-neutral-200 p-6">
        <div
          class="text-navy-400 mb-4 flex items-center gap-1.5 text-[10.5px] font-bold tracking-wider uppercase"
        >
          <svg
            class="text-navy-300 h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            viewBox="0 0 14 14"
          >
            <circle cx="7" cy="7" r="2.5" />
            <path
              d="M7 1v2M7 11v2M1 7h2M11 7h2M2.8 2.8l1.4 1.4M9.8 9.8l1.4 1.4M11.2 2.8l-1.4 1.4M4.2 9.8l-1.4 1.4"
            />
          </svg>
          Settings
        </div>

        <div>
          <label
            for="externalFileNumber"
            class="text-navy-700 mb-1.5 block text-[13px] font-semibold"
          >
            File Number
          </label>
          <input
            id="externalFileNumber"
            v-model="formData.externalFileNumber"
            name="externalFileNumber"
            type="text"
            :class="[
              'border-navy-100 text-navy-800 placeholder-navy-300 block w-full rounded-md border-[1.5px] px-3.5 py-2.5 text-sm transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10 focus:outline-none',
              formErrors?.externalFileNumber
                ? 'border-red-500 focus:border-red-500'
                : '',
            ]"
            placeholder="e.g. 2026-0142"
          />
          <p
            v-if="formErrors?.externalFileNumber"
            class="mt-1.5 flex items-center text-sm text-red-600"
          >
            <i class="pi pi-exclamation-circle mr-2 text-xs"></i>
            {{ formErrors.externalFileNumber }}
          </p>
        </div>

        <!-- ID Verification -->
        <div class="mt-5">
          <label
            :class="[
              'flex cursor-pointer items-center gap-3.5 rounded-[10px] border-[1.5px] p-4 transition-all',
              formData.verificationEnabled
                ? 'border-green-500 bg-green-50'
                : 'border-navy-100 bg-neutral-50',
            ]"
          >
            <div
              :class="[
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-md border transition-all',
                formData.verificationEnabled
                  ? 'border-green-500 bg-green-100'
                  : 'border-navy-100 bg-white',
              ]"
            >
              <svg
                :class="[
                  'h-[18px] w-[18px] transition-colors',
                  formData.verificationEnabled
                    ? 'text-green-700'
                    : 'text-navy-400',
                ]"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                viewBox="0 0 18 18"
              >
                <path
                  d="M9 1.5L2.5 4.5v4c0 4.5 3 7.5 6.5 9 3.5-1.5 6.5-4.5 6.5-9v-4L9 1.5z"
                />
                <path d="M6.5 9l2 2 3.5-4" />
              </svg>
            </div>
            <div class="flex-1">
              <p class="text-navy-800 text-[13.5px] font-semibold">
                ID Verification
              </p>
              <p class="text-navy-400 mt-0.5 text-xs">
                Require participants to verify identity with a photo ID and
                selfie
              </p>
            </div>
            <div class="relative h-6 w-11 shrink-0">
              <input
                v-model="formData.verificationEnabled"
                type="checkbox"
                class="peer sr-only"
              />
              <div
                class="bg-navy-200 h-6 w-11 rounded-full transition-colors peer-checked:bg-green-600"
              />
              <div
                class="absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5"
              />
            </div>
          </label>
        </div>

        <!-- Document Email -->
        <div class="mt-3">
          <label
            :class="[
              'flex cursor-pointer items-center gap-3.5 rounded-[10px] border-[1.5px] p-4 transition-all',
              formData.documentEmailEnabled
                ? 'border-green-500 bg-green-50'
                : 'border-navy-100 bg-neutral-50',
            ]"
          >
            <div
              :class="[
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-md border transition-all',
                formData.documentEmailEnabled
                  ? 'border-green-500 bg-green-100'
                  : 'border-navy-100 bg-white',
              ]"
            >
              <svg
                :class="[
                  'h-[18px] w-[18px] transition-colors',
                  formData.documentEmailEnabled
                    ? 'text-green-700'
                    : 'text-navy-400',
                ]"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                viewBox="0 0 18 18"
              >
                <rect x="2" y="4" width="14" height="10" rx="1.5" />
                <path d="M2 5l7 6 7-6" />
              </svg>
            </div>
            <div class="flex-1">
              <p class="text-navy-800 text-[13.5px] font-semibold">
                Document Email
              </p>
              <p class="text-navy-400 mt-0.5 text-xs">
                Email closing documents to participants after signing
              </p>
            </div>
            <div class="relative h-6 w-11 shrink-0">
              <input
                v-model="formData.documentEmailEnabled"
                type="checkbox"
                class="peer sr-only"
              />
              <div
                class="bg-navy-200 h-6 w-11 rounded-full transition-colors peer-checked:bg-green-600"
              />
              <div
                class="absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5"
              />
            </div>
          </label>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-2.5 border-t border-neutral-200 bg-neutral-50 px-6 py-4"
      >
        <button
          type="button"
          class="text-navy-500 hover:text-navy-700 hover:bg-navy-50 rounded-md px-5 py-2.5 text-[13.5px] font-semibold transition-all"
          @click="handleCancel"
        >
          Cancel
        </button>
        <AppButton
          type="submit"
          variant="secondary"
          :disabled="isSubmitting || !formData.officerId"
          :loading="isSubmitting"
          icon="pi pi-check"
        >
          {{ submitText }}
        </AppButton>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import { computed, reactive } from "vue"

import AppButton from "../../components/ui/AppButton.vue"
import { MemberOptionType, OrderFormProps, OrderFormType } from "./types"

const { order, submitText, members, isSubmitting, formErrors } = defineProps<{
  order: OrderFormProps
  submitText: string
  members: MemberOptionType[]
  isSubmitting?: boolean
  formErrors?: Record<string, string>
}>()

const formData = reactive<OrderFormType>({
  description: order.description || "",
  officerId: order.officer?.id || "",
  externalFileNumber: order.externalFileNumber || "",
  verificationEnabled: order.verificationEnabled || false,
  documentEmailEnabled: order.documentEmailEnabled || false,
})

const selectedOfficerInitials = computed(() => {
  const member = members.find((m) => m.id === formData.officerId)
  const name = member?.name ?? order.officer?.name ?? ""
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
})

const emit = defineEmits<{
  onSubmit: [formData: OrderFormType]
}>()

const submitForm = () => {
  emit("onSubmit", formData)
}

const handleCancel = () => {
  router.visit("/orders")
}
</script>
